import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * CATIA tarzı referans seçici — sabit XY/XZ/YZ (veya X/Y/Z) dropdown YERİNE.
 * Sol-tık: 3D sahneden referans seç (düzlem/yüz veya eksen/çizgi). Sağ-tık: standart hızlı menü.
 *
 * tur='duzlem' → düzlem referansı (element.duzlemRef idx veya element.aynaTip standart).
 * tur='eksen'  → eksen/yön referansı (element.eksenRef idx veya element.eksenTip standart).
 *
 * mod='datum' → feature düzenleme dialogları: 3D'den datum (duzlem3d/cizgi3d) seç → ref idx.
 * mod='komut' → komut penceresi: 3D'de yüz/çizgiye tıkla → en yakın standart (veya arbitrary ref).
 */
@Component({
  selector: 'fikoyt-duzlem-secici',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
  <div class="dz-sec">
    <button class="dz-ad-btn" (click)="menu($event)" [title]="(tur==='eksen' ? 'Standart eksen seç' : 'Standart düzlem seç') + ' ▾'">
      <span class="dz-ikon">{{ tur==='eksen' ? '↗' : '📐' }}</span><span class="dz-ad">{{ ad() }}</span><span class="dz-cev">▾</span>
    </button>
    <button class="dz-3d-btn" [class.dz-aktif]="aktif()" (click)="sec3d()"
            [title]="tur==='eksen' ? '3D sahneden eksen/çizgi seç' : '3D sahneden düzlem/yüz seç'">🎯</button>
    @if(menuAcik){
      <div class="dz-menu">
        @for(s of secenekler(); track s){ <button (click)="standart(s)">{{ s.toUpperCase() }}</button> }
      </div>
    }
  </div>`,
  styles: [`
    .dz-sec{ position:relative; flex:1; display:flex; gap:4px; }
    .dz-ad-btn{ display:flex; align-items:center; gap:6px; flex:1; padding:5px 8px; border-radius:7px;
      background:rgba(56,189,248,.12); border:1px solid rgba(56,189,248,.35); color:var(--py-metin,#e6f1ff);
      cursor:pointer; font-size:12px; transition:all .15s; }
    .dz-ad-btn:hover{ background:rgba(56,189,248,.22); border-color:rgba(56,189,248,.6); }
    .dz-ad{ font-weight:600; }
    .dz-cev{ margin-left:auto; opacity:.6; font-size:10px; }
    .dz-3d-btn{ flex:0 0 30px; border-radius:7px; background:rgba(56,189,248,.15); border:1px solid rgba(56,189,248,.4);
      color:var(--py-metin,#e6f1ff); cursor:pointer; font-size:14px; line-height:1; transition:all .15s; }
    .dz-3d-btn:hover{ background:rgba(56,189,248,.35); }
    .dz-3d-btn.dz-aktif{ background:rgba(250,204,21,.25); border-color:rgba(250,204,21,.7); animation:dzNabiz 1s infinite; }
    .dz-menu{ position:absolute; top:calc(100% + 4px); right:0; z-index:50; display:flex; gap:4px; padding:5px;
      background:rgba(15,23,42,.97); border:1px solid rgba(56,189,248,.4); border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,.5); }
    .dz-menu button{ padding:6px 12px; border-radius:6px; background:rgba(56,189,248,.15); border:1px solid rgba(56,189,248,.3);
      color:var(--py-metin,#e6f1ff); cursor:pointer; font-weight:700; font-size:12px; }
    .dz-menu button:hover{ background:rgba(56,189,248,.35); }
    @keyframes dzNabiz{ 0%,100%{ box-shadow:0 0 0 0 rgba(250,204,21,.4); } 50%{ box-shadow:0 0 0 4px rgba(250,204,21,0); } }
  `]
})
export class FikoDuzlemSecici {
  @Input() element: any;
  @Input() yonetici: any;
  @Input() anahtar = 'duzlemRef';
  /** 'duzlem': düzlem/yüz referansı · 'eksen': eksen/yön referansı. */
  @Input() tur: 'duzlem' | 'eksen' = 'duzlem';
  /** 'datum': feature düzenleme (datum ref idx) · 'komut': komut penceresi (yüz/çizgi → standart/arbitrary). */
  @Input() mod: 'datum' | 'komut' = 'datum';
  menuAcik = false;
  constructor(public CD: ChangeDetectorRef) {}

  secenekler(): ('xy' | 'xz' | 'yz' | 'x' | 'y' | 'z')[] {
    return this.tur === 'eksen' ? ['x', 'y', 'z'] : ['xy', 'xz', 'yz'];
  }
  ad(): string {
    if (!this.element) return this.tur === 'eksen' ? 'Z' : 'XY';
    return (this.tur === 'eksen'
      ? this.yonetici?.eksenAdi?.(this.element, this.anahtar)
      : this.yonetici?.duzlemAdi?.(this.element)) || (this.tur === 'eksen' ? 'Z' : 'XY');
  }
  aktif(): boolean { return this.yonetici?.refSecAktif && this.yonetici?.refSecHedef?.() === this.anahtar; }
  sec3d(): void {
    this.menuAcik = false;
    const cb = () => this.CD.markForCheck();
    if (this.mod === 'komut') {
      if (this.tur === 'eksen') this.yonetici?.komutEksenSec?.(this.element, this.anahtar, cb);
      else this.yonetici?.komutDuzlemYuzSec?.(this.element, this.anahtar, cb);
    } else {
      this.yonetici?.refSecBaslat?.(this.element, this.anahtar, this.tur === 'eksen' ? ['cizgi3d'] : ['duzlem3d'], cb);
    }
  }
  menu(e: MouseEvent): void { e.preventDefault(); this.menuAcik = !this.menuAcik; this.CD.markForCheck(); }
  standart(t: any): void {
    if (this.tur === 'eksen') this.yonetici?.eksenStandartSec?.(this.element, this.anahtar, t);
    else this.yonetici?.duzlemStandartSec?.(this.element, t);
    this.menuAcik = false; this.CD.markForCheck();
  }
}
